/* Merged Parcel Check — one tracking stamped on two parcels (medicine list repeats). */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Layers, Loader2, Scissors, RefreshCw, Undo2, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { API, getAuthHeader } from "@/App";

const api = (m, url, data) => axios({ method: m, url: `${API}/order-register${url}`, data, headers: getAuthHeader() }).then(r => r.data);
const errMsg = (e) => toast.error(e?.response?.data?.detail || "Something went wrong");

/* Every change needs: own login password, an admin's password or the supervisor password. */
const PasswordPrompt = ({ ask, onClose }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPw(""); }, [ask]);
  const go = async () => {
    setBusy(true);
    try { await ask.run(pw); onClose(); } catch (e) { errMsg(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ask} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="merged-pw-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4" /> {ask?.title}</DialogTitle>
          <DialogDescription>Enter your login password, an admin password or the supervisor password. Saved in the Audit Log.</DialogDescription>
        </DialogHeader>
        <Input type="password" autoFocus value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && pw && go()}
          placeholder="Password" data-testid="merged-pw-input" />
        <Button disabled={!pw || busy} onClick={go} data-testid="merged-pw-confirm">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Confirm</Button>
      </DialogContent>
    </Dialog>
  );
};

const Block = ({ b, i, entry, onSplit, confirm }) => {
  const [trk, setTrk] = useState(b.suggest || "");
  const keeper = i === (entry.keep || 0);
  const undo = (sid) => confirm("Undo split", (password) => api("post", `/merged-parcels/undo/${sid}`, { password })
    .then(() => { toast.success("Split undone"); onSplit(); }));
  const split = () => confirm(`Split parcel ${i + 1}`, async (password) => {
    const r = await api("post", "/merged-parcels/split", { tracking: entry.tracking, row_ids: b.row_ids, new_tracking: trk, password });
    toast.success(`Parcel ${i + 1} split off${trk ? ` to ${trk.toUpperCase()}` : " (tracking left blank)"} · ${r.or_rows} order rows · ${r.dba_rows} Database A rows`, {
      action: { label: "Undo", onClick: () => undo(r.split_id) }, duration: 12000 });
    onSplit();
  });
  return (
    <div className={`rounded-lg border p-2 ${keeper ? "bg-white" : "border-amber-300 bg-amber-50/60"}`} data-testid={`merged-block-${entry.tracking}-${i}`}>
      <div className="mb-1 text-[11px] font-semibold text-slate-600">Parcel {i + 1} · {b.meds.length} item(s){keeper && ` · keeps ${entry.tracking}`}</div>
      <div className="max-h-32 overflow-y-auto text-xs">{b.meds.map((m, k) => (
        <div key={k} className="flex justify-between gap-2 border-t border-slate-100 py-0.5"><span className="truncate">{m.second_name || m.medicine}</span><span className="text-slate-500">{m.qty}</span></div>))}</div>
      {!keeper && (<>
        {b.suggest && <div className="mt-1 text-[11px] text-emerald-700" data-testid={`merged-suggest-${entry.tracking}-${i}`}>Database A has {b.suggest} for these products — pre-filled</div>}
        <div className="mt-2 flex flex-wrap gap-1">
          <Input value={trk} onChange={e => setTrk(e.target.value)} placeholder="its own tracking (blank = later)" className="h-8 min-w-0 flex-1 font-mono text-xs" data-testid={`merged-new-trk-${entry.tracking}-${i}`} />
          <Button size="sm" className="h-8 bg-amber-600 text-xs text-white hover:bg-amber-700" onClick={split} data-testid={`merged-split-${entry.tracking}-${i}`}>
            <Scissors className="h-3.5 w-3.5" /> Split</Button>
        </div></>)}
    </div>
  );
};

const Entry = ({ e, reload, confirm }) => {
  const dismiss = () => confirm("Mark as one parcel", (password) => api("post", "/merged-parcels/dismiss", { tracking: e.tracking, password })
    .then(() => { toast.success("Marked as one parcel"); reload(); }));
  return (
    <div className="rounded-xl border bg-slate-50 p-3" data-testid={`merged-entry-${e.tracking}`}>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
        <b>{e.customer}</b>
        <span className="text-xs text-slate-500" data-testid={`merged-desc-${e.tracking}`}>
          {e.code} · {e.date} · {e.mode} · {e.invoice_no ? `${e.invoice_no}${e.invoice_date ? ` (inv. date ${e.invoice_date})` : ""}` : "no invoice"}</span>
        <span className="rounded bg-indigo-100 px-1.5 font-mono text-xs text-indigo-800">{e.tracking}</span>
        <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={dismiss} data-testid={`merged-dismiss-${e.tracking}`}>Not merged — one parcel</Button>
      </div>
      <div className="grid gap-2 md:grid-cols-2">{e.blocks.map((b, i) => <Block key={b.row_ids[0]} b={b} i={i} entry={e} onSplit={reload} confirm={confirm} />)}</div>
    </div>
  );
};

const fmt = (at) => new Date(/Z|[+-]\d\d:\d\d$/.test(at) ? at : `${at}Z`).toLocaleString();

const SplitHistory = ({ reloadKey, onUndone, confirm }) => {
  const [list, setList] = useState([]);
  const load = () => api("get", "/merged-parcels/splits").then(setList).catch(() => {});
  useEffect(() => { load(); }, [reloadKey]);
  const undo = (s) => confirm("Undo split", (password) => api("post", `/merged-parcels/undo/${s.id}`, { password }).then(r => {
    if (!r.or_rows && !r.dba_rows) toast.warning("Nothing to restore — those rows were changed again after the split");
    else toast.success(`Undone · ${r.or_rows} order rows back on ${s.tracking}`);
    load(); onUndone();
  }));
  if (!list.length) return null;
  return (
    <details className="rounded-xl border bg-white p-2 text-xs" open data-testid="merged-split-history">
      <summary className="cursor-pointer font-semibold text-slate-700">Recent splits ({list.length})</summary>
      <div className="mt-1 divide-y">{list.map(s => (
        <div key={s.id} className="flex flex-wrap items-center gap-2 py-1.5" data-testid={`merged-hist-${s.id}`}>
          <b>{s.customer}</b><span className="text-slate-500">{s.code}{s.invoice_no ? ` · ${s.invoice_no}${s.invoice_date ? ` (${s.invoice_date})` : ""}` : ""}</span>
          <span className="font-mono">{s.tracking} → {s.new_tracking || "(blank)"}</span>
          <span className="text-slate-500">{s.or_rows} rows · {s.dba_rows} DBA · {s.by}{s.confirmed_with ? ` · ${s.confirmed_with}` : ""} · {fmt(s.at)}</span>
          {s.undone ? <span className="ml-auto rounded bg-slate-100 px-1.5 text-slate-500">undone by {s.undone.by}</span>
            : <Button size="sm" variant="outline" className="ml-auto h-6 px-2 text-[11px]" onClick={() => undo(s)} data-testid={`merged-hist-undo-${s.id}`}>
              <Undo2 className="mr-1 h-3 w-3" /> Undo</Button>}
        </div>))}</div>
    </details>
  );
};

export const MergedParcelCheck = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [histKey, setHistKey] = useState(0);
  const [ask, setAsk] = useState(null);
  const confirm = (title, run) => setAsk({ title, run });
  const load = () => { setLoading(true); setHistKey(k => k + 1); api("get", "/merged-parcels?days=90").then(setData).catch(errMsg).finally(() => setLoading(false)); };
  return (
    <>
      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setOpen(true); load(); }} data-testid="merged-parcels-btn">
        <Layers className="mr-1 h-3.5 w-3.5" /> Merged parcels</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="merged-parcels-dialog">
          <DialogHeader>
            <DialogTitle>Merged Parcel Check</DialogTitle>
            <DialogDescription>Last 90 days, all couriers. One tracking whose medicine list repeats usually means two parcels were merged. Split off the other parcel and type its own tracking.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span data-testid="merged-count">{data ? `${data.count} tracking(s) to check` : ""}</span>
            <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={load} data-testid="merged-refresh"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh</Button>
          </div>
          {open && <SplitHistory reloadKey={histKey} confirm={confirm} onUndone={() => api("get", "/merged-parcels?days=90").then(setData).catch(errMsg)} />}
          {loading && <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>}
          {!loading && data && !data.count && <p className="py-8 text-center text-sm text-emerald-700" data-testid="merged-empty">No merged parcels found.</p>}
          {!loading && data?.rows.map(e => <Entry key={e.tracking + e.customer} e={e} reload={load} confirm={confirm} />)}
        </DialogContent>
      </Dialog>
      <PasswordPrompt ask={ask} onClose={() => setAsk(null)} />
    </>
  );
};
